<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { definePage } from "unplugin-vue-router/runtime";
import Loading from "@/components/shared/Loading.vue";
import MyOfficesTable from "@/components/models/practice/table/MyOfficesTable.vue";
import OrganizationSectionHero from "@/components/shared/headers/OrganizationSectionHero.vue";
import SectionCard from "@/components/ui/cards/SectionCard.vue";
import PageSearchFilter from "@/components/shared/table/PageSearchFilter.vue";
import FilterSelect, { type TFilterSelectOption } from "@/components/ui/inputs/FilterSelect.vue";
import { useCanAccessOfficesManagement } from "@/composables/shared/useSectionPermissions";
import { useOrganizationLevel, useOrganizationMode } from "@/composables/shared/useMyOrganization";
import { useCompanyMeList } from "@/composables/models/company/useCompanyMe";
import { ORGANIZATION_ACCENTS } from "@/components/shared/navbar/useNavbarSubItems";
import { formatCount } from "@/utils/Formatters";

definePage({
  meta: {
    title: "generic.fields.sections.offices",
    checkPrivileges: () => useCanAccessOfficesManagement().value,
  },
});

const { t } = useI18n();
const organizationMode = useOrganizationMode();
const { practices, msos, isList, isEmpty } = useOrganizationLevel("offices");
const { data: companyList, isLoading: isCompanyListLoading } = useCompanyMeList();
const companies = computed(() => companyList.value ?? []);

const officesSearch = ref<string | null>(null);
const officeCompanyFilter = ref<string | null>(null);

const companyOptions = computed<TFilterSelectOption<string>[]>(() =>
  companies.value.map((company) => ({ title: company.name, value: company.id }))
);

const ownerMso = computed(() => (msos.value.length === 1 ? msos.value[0] : undefined));
const ownerName = computed(() => ownerMso.value?.name);

// Patients are aggregated across every office, so that is the count carrying "in total" — the
// office count is just the length of the list on screen.
const patientCount = computed(() =>
  practices.value.reduce((sum, office) => sum + (office.patient_count ?? 0), 0)
);

const heroStats = computed(() => [
  {
    label: t("generic.fields.sections.offices"),
    value: formatCount(practices.value.length) ?? "0",
  },
  {
    label: t("generic.entity.patient", 2),
    value: t("generic.entity.inTotal", { entity: formatCount(patientCount.value) ?? "0" }),
  },
]);
</script>

<template>
  <div
    data-tour="tour-offices-list-page"
    class="tw:flex tw:flex-col tw:gap-5 tw:pb-4! tw:sm:pb-6!"
  >
    <organization-section-hero
      v-if="isList"
      :eyebrow="t('generic.fields.sections.offices')"
      :title="ownerName ?? t('generic.fields.sections.myOffices')"
      :mode="organizationMode"
      :mso="ownerMso"
      :accent-color="ORGANIZATION_ACCENTS.offices"
      icon="mdi-tooth-outline"
      :stats="heroStats"
    />

    <div
      v-if="isEmpty"
      class="tw:text-center tw:text-sm tw:text-on-surface/50 tw:py-16!"
    >
      {{ t("generic.messages.noEntity", { entity: t("generic.entity.practice", 2) }) }}
    </div>

    <Loading
      v-else-if="!isList"
      :size="32"
      class="tw:my-6!"
    />

    <section-card
      v-else
      icon="mdi-tooth-outline"
    >
      <template #title>
        {{ t("generic.fields.sections.offices") }}
        <span class="tw:text-[13px] tw:font-medium tw:text-on-surface/45">
          {{ `(${practices.length})` }}
        </span>
      </template>

      <template
        v-if="ownerName"
        #subtitle
      >
        <i18n-t
          keypath="generic.fields.sections.officesWithinDso"
          scope="global"
          tag="span"
        >
          <template #name>
            <strong>{{ ownerName }}</strong>
          </template>
        </i18n-t>
      </template>

      <template #actions>
        <div class="tw:flex tw:w-full tw:flex-wrap tw:items-end tw:gap-4 tw:md:w-auto">
          <div class="tw:w-full tw:md:w-[280px]">
            <page-search-filter
              v-model="officesSearch"
              :placeholder="t('generic.tables.searchByOfficeOrPracticeName')"
            />
          </div>

          <filter-select
            v-model="officeCompanyFilter"
            :items="companyOptions"
            query-key="practice"
            :label="t('generic.entity.company')"
            :loading="isCompanyListLoading"
            class="tw:w-full tw:sm:w-[240px]! tw:shrink-0"
          />
        </div>
      </template>

      <my-offices-table
        v-model:search="officesSearch"
        v-model:company-filter="officeCompanyFilter"
        :companies="companies"
      />
    </section-card>
  </div>
</template>
